<template>
  <section :aria-labelledby="`${panelId}-heading`">
    <div class="max-w-3xl">
      <h2
        :id="`${panelId}-heading`"
        class="text-2xl text-black dark:text-white"
      >
        {{ $t("pages.guide.recommendations") }}
      </h2>
      <p class="mt-2 text-sm leading-relaxed text-grey dark:text-grey-light">
        {{
          $t("pages.guide.recommendationsIntro", {
            name: guide.first_name,
          })
        }}
      </p>
    </div>

    <GuideEmptyState
      v-if="!hasRecommendationsContent"
      class="mt-6"
      icon="bulb"
      :title="$t('pages.guide.recommendationsEmptyTitle')"
      :description="$t('pages.guide.recommendationsEmptyDescription')"
    />

    <div v-else class="mt-8 space-y-10">
      <section
        :aria-labelledby="`${panelId}-personal-heading`"
        data-test="guide-recommendations-personal"
      >
        <div class="flex items-start gap-4">
          <span
            class="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gold/15"
            aria-hidden="true"
          >
            <Icon icon="bulb" class="text-lg text-black dark:text-white" />
          </span>
          <div>
            <h3
              :id="`${panelId}-personal-heading`"
              class="text-xl text-black dark:text-white"
              data-test="guide-recommendations-personal-title"
            >
              {{
                $t("pages.guide.recommendationsFromGuide", {
                  name: guide.first_name,
                })
              }}
            </h3>
          </div>
        </div>

        <ul
          v-if="guideRecommendations.length"
          class="mt-5 grid gap-4 lg:grid-cols-2"
        >
          <li
            v-for="recommendation in guideRecommendations"
            :key="recommendation.id"
            class="rounded-base border border-gold/35 bg-gold/5 p-5 dark:border-gold/25 dark:bg-gold/5"
          >
            <h4 class="text-lg text-black dark:text-white">
              {{ recommendation.title }}
            </h4>
            <div
              class="mt-2 text-sm leading-relaxed text-black/70 dark:text-white/70"
              v-html="recommendationHtml(recommendation.description)"
            ></div>
            <GuideYoutubeVideo
              v-if="recommendation.youtube_url"
              :video-url="recommendation.youtube_url"
              :title="recommendation.title"
              :active="props.isActive"
              class="mt-4 aspect-video overflow-hidden rounded-base"
            />
            <GuideRecommendationActions :actions="recommendation.actions" />
          </li>
        </ul>

        <div
          v-else
          class="mt-5 flex items-center gap-3 rounded-base border border-dashed border-light-300 bg-light-100 px-5 py-4 text-sm text-grey dark:border-grey-dark dark:bg-dark dark:text-grey-light"
          data-test="guide-recommendations-personal-empty"
        >
          <Icon icon="bulb" class="shrink-0 text-gold" aria-hidden="true" />
          <span>
            {{
              $t("pages.guide.recommendationsFromGuideEmpty", {
                name: guide.first_name,
              })
            }}
          </span>
        </div>
      </section>

      <section
        v-if="hasCityContent"
        :aria-labelledby="`${panelId}-city-heading`"
        class="border-t border-light-200 pt-8 dark:border-grey-dark"
        data-test="guide-recommendations-city"
      >
        <div class="flex items-start gap-4">
          <span
            class="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-black text-white dark:bg-white dark:text-black"
            aria-hidden="true"
          >
            <Icon icon="marker-pin" class="text-lg" />
          </span>
          <div>
            <h3
              :id="`${panelId}-city-heading`"
              class="text-xl text-black dark:text-white"
              data-test="guide-recommendations-city-title"
            >
              {{ $t("pages.guide.recommendationsFromWalkative") }}
            </h3>
          </div>
        </div>

        <div
          v-for="group in cityRecommendationGroups"
          :key="group.key"
          class="mt-6"
          :data-city-id="group.cityId ?? undefined"
          data-test="guide-recommendations-city-group"
        >
          <h4
            v-if="showCityGroupHeadings"
            class="mb-3 text-base font-bold text-black dark:text-white"
          >
            {{ group.cityName || $t("pages.guide.recommendationsOtherCities") }}
          </h4>
          <ul class="grid gap-4 lg:grid-cols-2">
            <li
              v-for="recommendation in group.recommendations"
              :key="`${recommendation.city_id}-${recommendation.title}`"
              class="rounded-base border border-light-200 bg-light-100 p-5 dark:border-grey-dark dark:bg-dark"
            >
              <span
                class="flex h-9 w-9 items-center justify-center rounded-full bg-black text-white dark:bg-white dark:text-black"
                aria-hidden="true"
              >
                <Icon icon="heart" class="text-base" />
              </span>
              <h5 class="mt-4 text-lg text-black dark:text-white">
                {{ recommendation.title }}
              </h5>
              <div
                class="mt-2 text-sm leading-relaxed text-black/70 dark:text-white/70"
                v-html="recommendationHtml(recommendation.description)"
              ></div>
              <GuideYoutubeVideo
                v-if="recommendation.youtube_url"
                :video-url="recommendation.youtube_url"
                :title="recommendation.title"
                :active="props.isActive"
                class="mt-4 aspect-video overflow-hidden rounded-base"
              />
              <GuideRecommendationActions :actions="recommendation.actions" />
            </li>
          </ul>
        </div>
      </section>
    </div>
  </section>
</template>

<script lang="ts" setup>
import type { Guide } from "@/types";
import { recommendationHtml } from "@/utils/recommendationHtml";

type CityRecommendation = NonNullable<Guide["city_recommendations"]>[number];

interface CityRecommendationGroup {
  key: string;
  cityId: number | null;
  cityName: string | null;
  recommendations: CityRecommendation[];
}

const props = withDefaults(
  defineProps<{
    guide: Guide;
    panelId: string;
    isActive?: boolean;
  }>(),
  { isActive: true },
);

const sortByOrderAndId = <T extends { id?: number | string; order?: number }>(
  items: T[],
) =>
  items
    .map((item, index) => ({ item, index }))
    .sort(
      (first, second) =>
        (first.item.order ?? first.index) -
          (second.item.order ?? second.index) ||
        String(first.item.id ?? first.index).localeCompare(
          String(second.item.id ?? second.index),
        ),
    )
    .map(({ item }) => item);

const guideRecommendations = computed(() =>
  sortByOrderAndId([...(props.guide.recommendations ?? [])]),
);

const primaryCity = computed(() => {
  const cities = props.guide.cities ?? [];
  return (
    cities.find((city) => city.id === props.guide.primary_city_id) ??
    cities.find((city) => city.name === props.guide.city) ??
    cities[0]
  );
});

const cityRecommendationGroups = computed<CityRecommendationGroup[]>(() => {
  const recommendations = props.guide.city_recommendations ?? [];
  const groupedByCity = new Map<number, CityRecommendation[]>();

  recommendations.forEach((recommendation) => {
    const group = groupedByCity.get(recommendation.city_id) ?? [];
    group.push(recommendation);
    groupedByCity.set(recommendation.city_id, group);
  });

  const knownCities = new Map(
    (props.guide.cities ?? []).map((city) => [city.id, city.name]),
  );
  const primaryCityId = props.guide.primary_city_id ?? primaryCity.value?.id;
  const orderedIds = [
    ...(primaryCityId !== undefined && primaryCityId !== null
      ? [primaryCityId]
      : []),
    ...(props.guide.cities ?? []).map((city) => city.id),
    ...groupedByCity.keys(),
  ].filter((id, index, ids) => ids.indexOf(id) === index);

  const groups: CityRecommendationGroup[] = [];
  const unknownRecommendations: CityRecommendation[] = [];

  orderedIds.forEach((cityId) => {
    const cityRecommendations = groupedByCity.get(cityId);
    if (!cityRecommendations?.length) return;

    const cityName =
      knownCities.get(cityId) ??
      (cityId === primaryCityId ? props.guide.city?.trim() : undefined);

    if (!cityName) {
      unknownRecommendations.push(...cityRecommendations);
      return;
    }

    groups.push({
      key: `city-${cityId}`,
      cityId,
      cityName,
      recommendations: sortByOrderAndId(cityRecommendations),
    });
  });

  if (unknownRecommendations.length) {
    groups.push({
      key: "other-cities",
      cityId: null,
      cityName: null,
      recommendations: sortByOrderAndId(unknownRecommendations),
    });
  }

  return groups;
});

const cityNames = computed(() => {
  const names = cityRecommendationGroups.value.flatMap((group) =>
    group.cityName ? [group.cityName] : [],
  );
  return [...new Set(names)];
});

const hasUnnamedCityGroup = computed(() =>
  cityRecommendationGroups.value.some((group) => !group.cityName),
);

const hasCityContent = computed(
  () => cityRecommendationGroups.value.length > 0,
);
const hasRecommendationsContent = computed(
  () => guideRecommendations.value.length > 0 || hasCityContent.value,
);
const showCityGroupHeadings = computed(
  () =>
    cityRecommendationGroups.value.length > 1 ||
    cityNames.value.length !== 1 ||
    hasUnnamedCityGroup.value,
);
</script>
